// ============================================================================
// FacilityTasksPane — embedded inside the Facility Detail "Tasks" tab.
//
// Replaces the read-only TaskBoard placeholder. Renders:
//   - Active tasks (Open + In_Progress) sorted by priority desc, due_date asc
//   - Collapsed Completed/Deferred section, expandable
//   - "New task" button → opens TaskForm with facility pre-filled
//   - Optimistic UI on complete / defer / reopen
//
// Owns its own data via useFacilityTasks. Does not bleed state into
// facility-detail.tsx — that file only knows the facility_id/account_id.
// ============================================================================

import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  EmptyState,
  ErrorState,
  Skeleton,
  useToast,
} from '../../shared/components';
import { useFacilityTasks, addComment } from '../tasks/hooks/use-task-data';
import TaskCard from '../tasks/task-card';
import TaskForm from '../tasks/task-form';
import { ResolveModal } from '../tasks/task-action-modals';

interface Props {
  facility_id: string;
  account_id: string | null;
}

export default function FacilityTasksPane({ facility_id, account_id }: Props) {
  const { employee } = useAuth();
  const toast = useToast();
  const {
    active,
    closed,
    isLoading,
    error,
    refetch,
    updateStatus,
    prependInserted,
  } = useFacilityTasks(facility_id);
  const [formOpen, setFormOpen] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [resolveTaskId, setResolveTaskId] = useState<string | null>(null);
  const myEid = employee?.employee_id ?? null;

  const onComplete = useCallback(
    async (id: string) => {
      try {
        await updateStatus(id, 'Done', {
          completed_by: employee?.employee_id ?? null,
        });
      } catch {
        /* optimistic UI inside updateStatus rolls back */
      }
    },
    [updateStatus, employee],
  );

  const onDefer = useCallback(
    async (id: string) => {
      const reason = window.prompt('Reason for deferral?') ?? '';
      try {
        await updateStatus(id, 'Deferred', { completion_notes: reason || null });
      } catch {
        /* noop */
      }
    },
    [updateStatus],
  );

  const onReopen = useCallback(
    async (id: string) => {
      try {
        await updateStatus(id, 'Open');
      } catch {
        /* noop */
      }
    },
    [updateStatus],
  );

  const onResolveSubmit = useCallback(
    async (status: 'Done' | 'Deferred', note: string) => {
      if (!resolveTaskId || !employee?.employee_id) return;
      await addComment(resolveTaskId, note, employee.employee_id);
      await updateStatus(resolveTaskId, status, {
        completed_by: employee.employee_id,
        completion_notes: note,
      });
      toast.success(`Task ${status === 'Done' ? 'resolved' : 'deferred'}`, {
        description: resolveTaskId,
      });
    },
    [resolveTaskId, employee, updateStatus, toast],
  );

  const resolveTask = useMemo(
    () =>
      [...active, ...closed].find((t) => t.task_id === resolveTaskId) ?? null,
    [active, closed, resolveTaskId],
  );

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-sm text-tcs-text-muted">
          {active.length} active · {closed.length} closed
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={refetch} aria-label="Reload">
            <RefreshCw size={14} className="mr-1" /> Reload
          </Button>
          <Button onClick={() => setFormOpen(true)}>
            <Plus size={14} className="mr-1" />
            New task
          </Button>
        </div>
      </div>

      {error && (
        <ErrorState
          title="Couldn't load tasks for this facility"
          body={error}
          onRetry={refetch}
        />
      )}

      {isLoading && !error && (
        <div className="space-y-2">
          <Skeleton block height={64} />
          <Skeleton block height={64} />
        </div>
      )}

      {!isLoading && !error && active.length === 0 && (
        <EmptyState
          title="No active tasks"
          body="Create one with the New task button above."
        />
      )}

      {active.length > 0 && (
        <ul className="space-y-2">
          {active.map((t) => (
            <TaskCard
              key={t.task_id}
              task={t}
              currentEid={myEid}
              onResolve={(id) => setResolveTaskId(id)}
              onComplete={onComplete}
              onDefer={onDefer}
              onReopen={onReopen}
            />
          ))}
        </ul>
      )}

      {closed.length > 0 && (
        <div className="mt-6">
          <button
            type="button"
            className="flex items-center gap-1 text-xs uppercase tracking-wide text-tcs-text-muted hover:text-tcs-text"
            onClick={() => setShowClosed((s) => !s)}
          >
            {showClosed ? (
              <ChevronDown size={14} />
            ) : (
              <ChevronRight size={14} />
            )}
            Closed ({closed.length})
          </button>
          {showClosed && (
            <ul className="mt-2 space-y-2 opacity-80">
              {closed.map((t) => (
                <TaskCard
                  key={t.task_id}
                  task={t}
                  onReopen={onReopen}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      <TaskForm
        open={formOpen}
        presetFacility={{ facility_id, account_id }}
        onClose={() => setFormOpen(false)}
        onSaved={(row, mode) => {
          if (mode === 'created') prependInserted(row);
          refetch();
        }}
      />

      <ResolveModal
        open={resolveTaskId !== null}
        taskId={resolveTaskId}
        taskDescription={resolveTask?.description}
        onClose={() => setResolveTaskId(null)}
        onSubmit={onResolveSubmit}
      />
    </div>
  );
}
